import { useEffect, useState, useCallback } from "react";
import {
  Loader2, Search, Plus, ArrowUp, ArrowDown, Store, Package, ListChecks, Tag,
  ChevronLeft, ChevronRight, Save, X, MapPin, AlertTriangle, Building2, Pencil, Trash2, History, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { rupiah, rupiahShort, fmtDate, fmtDateTime, slug } from "@/lib/format";
import { Modal } from "@/components/Modal";
import { CustomerArCard } from "@/components/finance/CustomerArCard";

export default function Customers() {
  const { hasPerm } = useAuth();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [sort, setSort] = useState("name");
  const [direction, setDirection] = useState("asc");
  const [detail, setDetail] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [productOpen, setProductOpen] = useState(false);
  const [pricelistMgrOpen, setPricelistMgrOpen] = useState(false);
  const [createPlOpen, setCreatePlOpen] = useState(false);
  const [editPlId, setEditPlId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/customers", { params: { page, page_size: 10, q: q || undefined, sort, direction, customer_type: typeFilter } });
      setRows(r.data.data); setMeta(r.data.meta);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  }, [page, q, sort, direction, typeFilter]);

  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const toggleSort = (field) => {
    if (sort === field) setDirection((d) => (d === "asc" ? "desc" : "asc"));
    else { setSort(field); setDirection("asc"); }
  };

  const markDistributor = async (c, val) => {
    try {
      await api.patch(`/customers/${c.odoo_customer_id}`, { customer_type: val ? "Distributor" : "GT", sync_now: true });
      toast.success(val ? `${c.name} ditandai sebagai Distributor` : "Status distributor dibatalkan");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const changeType = async (c, val) => {
    if (val === c.customer_type) return;
    try {
      await api.patch(`/customers/${c.odoo_customer_id}`, { customer_type: val, sync_now: true });
      toast.success(`Tipe ${c.name} diubah ke ${val}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="customers-page">
      <div className="section-head compact">
        <div><p className="eyebrow">MASTER DATA</p><h2>Customer & Produk</h2></div>
        <div className="team-header-actions">
          <button className="rj-btn rj-btn-ghost" onClick={() => setProductOpen(true)} data-testid="open-product-master-btn"><Package size={16} /> Master Produk</button>
          <button className="rj-btn rj-btn-ghost" onClick={() => setPricelistMgrOpen(true)} data-testid="open-pricelist-btn"><Tag size={16} /> Pricelist</button>
          {hasPerm("customers:create") && <button className="rj-btn rj-btn-primary" onClick={() => setCreateOpen(true)} data-testid="open-create-customer-btn"><Plus size={16} /> Customer</button>}
        </div>
      </div>

      <div className="panel table-panel customer-table">
        <div className="table-tools">
          <div className="search-box"><Search /><input data-testid="customer-search" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Cari nama customer…" /></div>
          <select className="rj-select" style={{ width: 160 }} value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }} data-testid="customer-type-filter">
            <option value="all">Semua Tipe</option>
            <option value="Distributor">Distributor</option>
            <option value="MT">MT</option>
            <option value="GT">GT</option>
          </select>
          <div className="customer-sort-tools">
            {[["name", "Nama"], ["area", "Area"], ["type", "Tipe"], ["last_order", "Order Terakhir"]].map(([f, label]) => (
              <button key={f} className={sort === f ? "active" : ""} onClick={() => toggleSort(f)} data-testid={`sort-${f}`}>
                {label} {sort === f && (direction === "asc" ? <ArrowUp size={13} /> : <ArrowDown size={13} />)}
              </button>
            ))}
          </div>
        </div>

        {loading ? <div className="rj-loading"><Loader2 className="spin" /> Memuat customer…</div> : rows.length === 0 ? (
          <div className="rj-empty"><Store /><p>Tidak ada customer.</p></div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
              <thead><tr style={{ background: "#faf8f7", fontSize: 11, color: "#817974", textAlign: "left" }}>
                <th style={{ padding: 12 }}>Customer</th><th style={{ padding: 12 }}>Tipe</th><th style={{ padding: 12 }}>Area</th>
                <th style={{ padding: 12 }}>Pricelist</th><th style={{ padding: 12 }}>AR Term</th><th style={{ padding: 12 }}>Pemenuhan</th><th style={{ padding: 12 }}>Order Terakhir</th><th style={{ padding: 12 }}>Distributor</th>
              </tr></thead>
              <tbody>
                {rows.map((c) => (
                  <tr key={c.odoo_customer_id} style={{ borderTop: "1px solid #f0ecea" }} data-testid={`customer-row-${c.odoo_customer_id}`}>
                    <td style={{ padding: 12 }}>
                      <button className="customer-open-button" onClick={() => setDetail(c)} data-testid={`customer-open-${c.odoo_customer_id}`}>
                        <strong>{c.name}</strong><small>{c.email || "-"}</small>
                      </button>
                    </td>
                    <td style={{ padding: 12 }}>
                      {hasPerm("customers:create") ? (
                        <select className={`rj-select customer-type-inline ${slug(c.customer_type)}`} value={c.customer_type}
                          onChange={(e) => changeType(c, e.target.value)} data-testid={`customer-type-select-${c.odoo_customer_id}`}>
                          <option value="Distributor">Distributor</option>
                          <option value="MT">MT</option>
                          <option value="GT">GT</option>
                        </select>
                      ) : (
                        <span className={`customer-type ${slug(c.customer_type)}`}>{c.customer_type}</span>
                      )}
                    </td>
                    <td style={{ padding: 12, fontSize: 13 }}>{c.area}</td>
                    <td style={{ padding: 12, fontSize: 12 }}>
                      {c.pricelist_odoo_id ? (
                        <button className="pl-link" onClick={() => setEditPlId(c.pricelist_odoo_id)} data-testid={`customer-pricelist-${c.odoo_customer_id}`}>{c.price_list || "—"}</button>
                      ) : (c.price_list || "—")}
                    </td>
                    <td style={{ padding: 12, fontSize: 12 }} data-testid={`customer-ar-term-${c.odoo_customer_id}`}>{c.ar_term_name || "Immediate"}{c.ar_term_days && !c.ar_term_eom ? <small style={{ display: "block", color: "#8b837f" }}>{c.ar_term_days} hari</small> : null}</td>
                    <td style={{ padding: 12, fontSize: 12 }}>{c.fulfillment_mode === "direct" ? "Direct" : "Distributor"}</td>
                    <td style={{ padding: 12, fontSize: 12 }}>{c.last_order_at ? fmtDate(c.last_order_at) : "-"}</td>
                    <td style={{ padding: 12 }}>
                      {c.customer_type === "Distributor" ? (
                        <button className="dist-toggle on" onClick={() => markDistributor(c, false)} data-testid={`unmark-distributor-${c.odoo_customer_id}`} title="Batalkan status distributor">
                          <Building2 size={12} /> Distributor
                        </button>
                      ) : (
                        hasPerm("customers:create") && (
                          <button className="dist-toggle" onClick={() => markDistributor(c, true)} data-testid={`mark-distributor-${c.odoo_customer_id}`}>
                            + Jadikan Distributor
                          </button>
                        )
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="product-master-footer" style={{ padding: "14px 18px" }}>
          <span className="customer-result-count">{meta.total} customer · halaman {page}/{meta.pages}</span>
          <div style={{ display: "flex", gap: 6 }}>
            <button className="rj-btn rj-btn-ghost rj-btn-sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="cust-prev"><ChevronLeft size={15} /></button>
            {Array.from({ length: meta.pages }, (_, i) => i + 1).slice(0, 6).map((n) => (
              <button key={n} className={`rj-btn rj-btn-sm ${n === page ? "rj-btn-green" : "rj-btn-ghost"}`} onClick={() => setPage(n)} data-testid={`cust-page-${n}`}>{n}</button>
            ))}
            <button className="rj-btn rj-btn-ghost rj-btn-sm" disabled={page >= meta.pages} onClick={() => setPage((p) => p + 1)} data-testid="cust-next"><ChevronRight size={15} /></button>
          </div>
        </div>
      </div>

      {detail && <CustomerDetail customer={detail} onClose={() => { setDetail(null); load(); }} onSaved={() => { setDetail(null); load(); }} />}
      {createOpen && <CreateCustomer onClose={() => setCreateOpen(false)} onSaved={() => { setCreateOpen(false); load(); }} />}
      {productOpen && <ProductMaster onClose={() => setProductOpen(false)} />}
      {pricelistMgrOpen && <PricelistManager onClose={() => setPricelistMgrOpen(false)} onEdit={(id) => { setPricelistMgrOpen(false); setEditPlId(id); }} onCreate={() => { setPricelistMgrOpen(false); setCreatePlOpen(true); }} />}
      {createPlOpen && <CreatePricelist onClose={() => setCreatePlOpen(false)} />}
      {editPlId != null && <PricelistEditor odooId={editPlId} onClose={() => setEditPlId(null)} onSaved={() => { setEditPlId(null); load(); }} />}
    </div>
  );
}

function CustomerDetail({ customer, onClose, onSaved }) {
  const { hasPerm } = useAuth();
  const [pricelists, setPricelists] = useState([]);
  const [form, setForm] = useState({
    credit_limit: customer.credit_limit, pricelist_odoo_id: customer.pricelist_odoo_id,
    customer_type: customer.customer_type, fulfillment_mode: customer.fulfillment_mode,
    email: customer.email || "", phone: customer.phone || "",
  });
  const [saving, setSaving] = useState(false);
  const [addresses, setAddresses] = useState(customer.delivery_addresses || []);
  const [addr, setAddr] = useState({ name: "", street: "", city: "", phone: "" });
  const [addingAddr, setAddingAddr] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", street: "", city: "", phone: "" });
  const [busyAddr, setBusyAddr] = useState(false);
  useEffect(() => { api.get("/odoo/pricelists").then((r) => setPricelists(r.data.data)); }, []);

  const save = async (syncNow) => {
    setSaving(true);
    try {
      await api.patch(`/customers/${customer.odoo_customer_id}`, { ...form, sync_now: syncNow });
      toast.success(syncNow ? "Profil disinkronkan ke Odoo" : "Perubahan disimpan sebagai draft");
      if (syncNow) onSaved(); 
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const addAddress = async () => {
    if (!addr.name.trim()) { toast.error("Nama alamat wajib diisi"); return; }
    setAddingAddr(true);
    try {
      const r = await api.post(`/customers/${customer.odoo_customer_id}/addresses`, addr);
      setAddresses(r.data.data.delivery_addresses || []);
      setAddr({ name: "", street: "", city: "", phone: "" });
      if (r.data.meta?.odoo_warning) toast.warning(r.data.meta.odoo_warning);
      else toast.success(r.data.meta?.odoo_synced ? "Alamat ditambahkan & disinkronkan ke Odoo" : "Alamat ditambahkan");
    } catch (e) { toast.error(apiError(e)); } finally { setAddingAddr(false); }
  };

  const startEdit = (a) => { setEditingId(a.id); setEditForm({ name: a.name || "", street: a.street || "", city: a.city || "", phone: a.phone || "" }); };
  const saveEdit = async (a) => {
    setBusyAddr(true);
    try {
      const r = await api.patch(`/customers/${customer.odoo_customer_id}/addresses/${a.id}`, editForm);
      setAddresses(r.data.data.delivery_addresses || []);
      setEditingId(null);
      if (r.data.meta?.odoo_warning) toast.warning(r.data.meta.odoo_warning);
      else toast.success("Alamat diperbarui");
    } catch (e) { toast.error(apiError(e)); } finally { setBusyAddr(false); }
  };
  const removeAddr = async (a) => {
    if (!window.confirm(`Nonaktifkan alamat "${a.name}"? Alamat akan diarsipkan di Odoo.`)) return;
    try {
      const r = await api.delete(`/customers/${customer.odoo_customer_id}/addresses/${a.id}`);
      setAddresses(r.data.data.delivery_addresses || []);
      if (r.data.meta?.odoo_warning) toast.warning(r.data.meta.odoo_warning);
      else toast.success("Alamat dinonaktifkan");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Modal open onClose={onClose} className="customer-detail-dialog" title={customer.name}
      subtitle={`${customer.customer_type} · ${customer.area}`} testId="customer-detail-dialog"
      footer={hasPerm("customers:create") && (
        <>
          <button className="rj-btn rj-btn-ghost" onClick={() => save(false)} disabled={saving}>Simpan Draft</button>
          <button className="rj-btn rj-btn-green" onClick={() => save(true)} disabled={saving} data-testid="sync-profile-btn">
            {saving ? <Loader2 size={16} className="spin" /> : <><Save size={16} /> Simpan & Sinkronkan Profil</>}
          </button>
        </>
      )}>
      <div className="customer-detail-form">
        <div className="customer-detail-identity">
          <div><span>{customer.name.slice(0, 2).toUpperCase()}</span>
            <div><strong>{customer.name}</strong><small>Credit limit {rupiahShort(customer.credit_limit)} · {customer.fulfillment_mode === "direct" ? "Direct" : "Distributor"}</small></div>
          </div>
        </div>
        <div className="customer-detail-grid">
          <label>Tipe Toko
            <select className="rj-select" value={form.customer_type} onChange={(e) => setForm({ ...form, customer_type: e.target.value })} data-testid="detail-type">
              <option>Distributor</option><option>MT</option><option>GT</option>
            </select>
          </label>
          <label>Pricelist
            <select className="rj-select" value={form.pricelist_odoo_id} onChange={(e) => setForm({ ...form, pricelist_odoo_id: Number(e.target.value) })} data-testid="detail-pricelist">
              {pricelists.map((p) => <option key={p.odoo_id} value={p.odoo_id}>{p.name}</option>)}
            </select>
          </label>
          <label>Credit Limit<input className="rj-input" type="number" value={form.credit_limit} onChange={(e) => setForm({ ...form, credit_limit: Number(e.target.value) })} data-testid="detail-credit" /></label>
          <label>Jalur Pemenuhan
            <select className="rj-select" value={form.fulfillment_mode} onChange={(e) => setForm({ ...form, fulfillment_mode: e.target.value })} data-testid="detail-fulfillment">
              <option value="direct">Direct Rasa Juara</option><option value="distributor">Melalui Distributor</option>
            </select>
          </label>
          <label>Email<input className="rj-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></label>
          <label>Telepon<input className="rj-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></label>
        </div>
        <CustomerArCard customerId={customer.odoo_customer_id} />
        <ArTermField customer={customer} />
        <div className="customer-address-summary">
          <div className="customer-address-head"><span><strong>Alamat Pengiriman</strong><small>{addresses.length} alamat</small></span></div>
          {addresses.map((a) => (
            <article key={a.id} className={a.primary ? "customer-address-primary" : ""} data-testid={`address-item-${a.odoo_id || a.id}`}>
              <MapPin />
              {editingId === a.id ? (
                <div className="address-edit-inline">
                  <input className="rj-input" placeholder="Nama alamat" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="addr-edit-name" />
                  <input className="rj-input" placeholder="Jalan / alamat" value={editForm.street} onChange={(e) => setEditForm({ ...editForm, street: e.target.value })} data-testid="addr-edit-street" />
                  <input className="rj-input" placeholder="Kota" value={editForm.city} onChange={(e) => setEditForm({ ...editForm, city: e.target.value })} data-testid="addr-edit-city" />
                  <input className="rj-input" placeholder="Telepon" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} data-testid="addr-edit-phone" />
                  <div className="address-edit-actions">
                    <button className="rj-btn rj-btn-green rj-btn-sm" onClick={() => saveEdit(a)} disabled={busyAddr} data-testid="addr-edit-save">{busyAddr ? <Loader2 size={13} className="spin" /> : "Simpan"}</button>
                    <button className="rj-btn rj-btn-ghost rj-btn-sm" onClick={() => setEditingId(null)}>Batal</button>
                  </div>
                </div>
              ) : (
                <>
                  <div><strong>{a.name}</strong><small>{a.street}{a.city ? `, ${a.city}` : ""}</small>
                    {a.odoo_id && <small className="address-odoo-id">Tersinkron Odoo #{a.odoo_id}</small>}
                    {a.last_order_at && <small className="address-last-order">Order terakhir {fmtDate(a.last_order_at)}</small>}
                  </div>
                  {hasPerm("customers:create") && (
                    <div className="address-actions">
                      <button className="rj-icon-btn" onClick={() => startEdit(a)} data-testid={`addr-edit-${a.odoo_id || a.id}`} title="Edit alamat"><Pencil size={14} /></button>
                      {!a.primary && <button className="rj-icon-btn danger" onClick={() => removeAddr(a)} data-testid={`addr-delete-${a.odoo_id || a.id}`} title="Nonaktifkan alamat"><Trash2 size={14} /></button>}
                    </div>
                  )}
                </>
              )}
            </article>
          ))}
          {hasPerm("customers:create") && (
            <div className="address-add-form" data-testid="address-add-form">
              <div className="address-add-head"><Plus size={14} /> Tambah Alamat Kirim (disinkronkan ke Odoo)</div>
              <div className="address-add-grid">
                <input className="rj-input" placeholder="Nama alamat (cth: Gudang Cikarang)" value={addr.name} onChange={(e) => setAddr({ ...addr, name: e.target.value })} data-testid="addr-name" />
                <input className="rj-input" placeholder="Jalan / alamat" value={addr.street} onChange={(e) => setAddr({ ...addr, street: e.target.value })} data-testid="addr-street" />
                <input className="rj-input" placeholder="Kota" value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })} data-testid="addr-city" />
                <input className="rj-input" placeholder="Telepon" value={addr.phone} onChange={(e) => setAddr({ ...addr, phone: e.target.value })} data-testid="addr-phone" />
              </div>
              <button className="rj-btn rj-btn-green rj-btn-sm" onClick={addAddress} disabled={addingAddr} data-testid="add-address-btn">
                {addingAddr ? <Loader2 size={14} className="spin" /> : <><Plus size={14} /> Tambah & Sinkronkan Alamat</>}
              </button>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

function ArTermField({ customer }) {
  const { hasPerm } = useAuth();
  const [terms, setTerms] = useState([]);
  const [termId, setTermId] = useState(customer.ar_term_odoo_id ? String(customer.ar_term_odoo_id) : "");
  const [current, setCurrent] = useState({ name: customer.ar_term_name, days: customer.ar_term_days, eom: customer.ar_term_eom });
  const [saving, setSaving] = useState(false);
  const canEdit = hasPerm("customers:ar_term");
  useEffect(() => { api.get("/finance/payment-terms").then((r) => setTerms(r.data.data)).catch(() => {}); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch(`/customers/${customer.odoo_customer_id}/ar-term`, { payment_term_odoo_id: termId ? Number(termId) : null });
      setCurrent({ name: r.data.data.ar_term_name, days: r.data.data.ar_term_days, eom: r.data.data.ar_term_eom });
      toast.success(r.data.meta?.odoo_synced ? "AR Term disimpan & disinkronkan ke Odoo" : "AR Term disimpan");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <div className="ar-term-box" data-testid="ar-term-box">
      <div><strong>AR Term (Termin Pembayaran)</strong>
        <small data-testid="ar-term-current">Saat ini: {current.name || "Immediate"}{current.eom ? "" : ` · ${current.days || 0} hari`} — dipakai untuk jatuh tempo invoice & AR Aging</small></div>
      {canEdit && (
        <div className="ar-term-edit">
          <select className="rj-select" value={termId} onChange={(e) => setTermId(e.target.value)} data-testid="ar-term-select">
            <option value="">— Kosongkan termin —</option>
            {terms.map((t) => <option key={t.odoo_id} value={t.odoo_id}>{t.name}{t.eom ? "" : ` (${t.days} hari)`}</option>)}
          </select>
          <button className="rj-btn rj-btn-green rj-btn-sm" onClick={save} disabled={saving} data-testid="ar-term-save-btn">
            {saving ? <Loader2 size={14} className="spin" /> : <><Save size={14} /> Simpan AR Term</>}
          </button>
        </div>
      )}
    </div>
  );
}

function CreateCustomer({ onClose, onSaved }) {
  const [pricelists, setPricelists] = useState([]);
  const [form, setForm] = useState({ name: "", customer_type: "GT", area: "DKI Jakarta", pricelist_odoo_id: 1, credit_limit: 0, fulfillment_mode: "direct", email: "", phone: "", vat: "", street: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/odoo/pricelists").then((r) => setPricelists(r.data.data)); }, []);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Nama customer wajib diisi"); return; }
    setSaving(true);
    try { await api.post("/customers", form); toast.success("Customer dibuat & didorong ke Odoo"); onSaved(); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <Modal open onClose={onClose} className="customer-create-dialog" title="Tambah Customer"
      subtitle="Customer baru akan didorong sebagai res.partner ke Odoo" testId="create-customer-dialog"
      footer={<>
        <button className="rj-btn rj-btn-ghost" onClick={onClose}>Batal</button>
        <button className="rj-btn rj-btn-primary" onClick={save} disabled={saving} data-testid="submit-customer-btn">{saving ? <Loader2 size={16} className="spin" /> : "Simpan Customer"}</button>
      </>}>
      <div className="customer-create-form">
        <section>
          <div className="customer-create-section-title"><strong>Profil Toko</strong></div>
          <div className="customer-create-grid">
            <label className="span-2">Nama Toko<input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cust-name" /></label>
            <label>Tipe<select className="rj-select" value={form.customer_type} onChange={(e) => setForm({ ...form, customer_type: e.target.value })}><option>Distributor</option><option>MT</option><option>GT</option></select></label>
            <label>Area<input value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} /></label>
            <label>Pricelist<select className="rj-select" value={form.pricelist_odoo_id} onChange={(e) => setForm({ ...form, pricelist_odoo_id: Number(e.target.value) })}>{pricelists.map((p) => <option key={p.odoo_id} value={p.odoo_id}>{p.name}</option>)}</select></label>
            <label>Credit Limit<input type="number" value={form.credit_limit} onChange={(e) => setForm({ ...form, credit_limit: Number(e.target.value) })} /></label>
            <label>Jalur Pemenuhan<select className="rj-select" value={form.fulfillment_mode} onChange={(e) => setForm({ ...form, fulfillment_mode: e.target.value })}><option value="direct">Direct Rasa Juara</option><option value="distributor">Melalui Distributor</option></select></label>
            <label>VAT / NPWP<input value={form.vat} onChange={(e) => setForm({ ...form, vat: e.target.value })} /></label>
            <label>Email<input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></label>
            <label>Telepon<input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></label>
            <label className="span-2">Alamat Utama<input value={form.street} onChange={(e) => setForm({ ...form, street: e.target.value })} /></label>
          </div>
        </section>
      </div>
    </Modal>
  );
}

function ProductMaster({ onClose }) {
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/odoo/master").then((r) => { setProducts(r.data.data.products); setLoading(false); }); }, []);

  const toggle = async (p, val) => {
    setProducts((ps) => ps.map((x) => x.odoo_id === p.odoo_id ? { ...x, sales_hub_enabled: val } : x));
    try { await api.patch("/odoo/master", { product_odoo_id: p.odoo_id, sales_hub_enabled: val }); }
    catch (e) { toast.error(apiError(e)); }
  };
  const filtered = products.filter((p) => !q || p.name.toLowerCase().includes(q.toLowerCase()) || (p.default_code || "").toLowerCase().includes(q.toLowerCase()));
  const selected = products.filter((p) => p.sales_hub_enabled).length;

  const setAll = async (val) => {
    const ids = filtered.map((p) => p.odoo_id);
    if (!ids.length) return;
    const idSet = new Set(ids);
    setProducts((ps) => ps.map((x) => idSet.has(x.odoo_id) ? { ...x, sales_hub_enabled: val } : x));
    try { await api.patch("/odoo/master/bulk", { product_odoo_ids: ids, sales_hub_enabled: val }); }
    catch (e) { toast.error(apiError(e)); }
  };

  const activateAllSaleOk = async () => {
    const ids = products.map((p) => p.odoo_id);
    if (!ids.length) return;
    setProducts((ps) => ps.map((x) => ({ ...x, sales_hub_enabled: true })));
    try { await api.patch("/odoo/master/bulk", { product_odoo_ids: ids, sales_hub_enabled: true }); toast.success(`${ids.length} produk sale_ok diaktifkan`); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Modal open onClose={onClose} className="pricelist-create-dialog" title="Master Produk Sales Hub"
      subtitle="Pilih produk saleable yang tampil di Sales Hub" testId="product-master-dialog">
      <div className="product-master-tools">
        <div className="master-search"><Search /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari SKU / nama…" data-testid="product-search" /></div>
        <div className="product-master-actions">
          <span className="selected-product-count"><ListChecks size={14} /> {selected} aktif</span>
          <button type="button" className="master-select-btn primary" onClick={activateAllSaleOk} data-testid="product-activate-all-btn">Aktifkan Semua ({products.length})</button>
          <button type="button" className="master-select-btn" onClick={() => setAll(true)} data-testid="product-select-all-btn">Pilih Semua</button>
          <button type="button" className="master-select-btn danger" onClick={() => setAll(false)} data-testid="product-uncheck-all-btn">Uncheck All</button>
        </div>
      </div>
      {loading ? <div className="rj-loading"><Loader2 className="spin" /> Memuat…</div> : (
        <div className="product-check-list">
          {filtered.map((p) => (
            <label key={p.odoo_id} data-testid={`product-toggle-${p.odoo_id}`}>
              <input type="checkbox" checked={!!p.sales_hub_enabled} onChange={(e) => toggle(p, e.target.checked)} />
              <span><strong>{p.name}</strong><small>{p.default_code} · {rupiah(p.list_price)} · {p.uom}</small></span>
            </label>
          ))}
        </div>
      )}
    </Modal>
  );
}

function CreatePricelist({ onClose }) {
  const [products, setProducts] = useState([]);
  const [name, setName] = useState("");
  const [prices, setPrices] = useState({});
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/odoo/master").then((r) => setProducts(r.data.data.products.filter((p) => p.sales_hub_enabled))); }, []);

  const save = async () => {
    if (!name.trim()) { toast.error("Nama pricelist wajib diisi"); return; }
    const items = Object.entries(prices).filter(([, v]) => v > 0).map(([pid, v]) => ({ product_odoo_id: Number(pid), fixed_price: Number(v) }));
    setSaving(true);
    try { await api.post("/odoo/pricelists", { name, currency: "IDR", items }); toast.success("Pricelist dibuat & disinkronkan"); onClose(); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const filtered = products.filter((p) => !q || p.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <Modal open onClose={onClose} className="pricelist-create-dialog" title="Buat Pricelist"
      subtitle="Tentukan harga per produk lalu simpan ke Odoo" testId="create-pricelist-dialog"
      footer={<>
        <button className="rj-btn rj-btn-ghost" onClick={onClose}>Batal</button>
        <button className="rj-btn rj-btn-green" onClick={save} disabled={saving} data-testid="submit-pricelist-btn">{saving ? <Loader2 size={16} className="spin" /> : "Simpan Pricelist"}</button>
      </>}>
      <div className="pricelist-create-form">
        <div className="pricelist-create-top">
          <label>Nama Pricelist<input value={name} onChange={(e) => setName(e.target.value)} placeholder="cth: Promo Ramadhan" data-testid="pricelist-name" /></label>
          <label>Mata Uang<input value="IDR" disabled /></label>
        </div>
        <label className="pricelist-search"><Search /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari produk…" /></label>
        <div className="pricelist-product-list">
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.odoo_id} style={{ borderBottom: "1px solid #eef2f0" }}>
                  <td style={{ padding: 10 }}><strong style={{ fontSize: 13 }}>{p.name}</strong><small style={{ display: "block", color: "#718078" }}>{p.default_code} · list {rupiah(p.list_price)}</small></td>
                  <td style={{ padding: 10 }}>
                    <div className="price-input"><b>Rp</b><input type="number" placeholder={p.list_price} value={prices[p.odoo_id] || ""} onChange={(e) => setPrices((s) => ({ ...s, [p.odoo_id]: e.target.value }))} data-testid={`pl-price-${p.odoo_id}`} /></div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Modal>
  );
}

function PricelistManager({ onClose, onEdit, onCreate }) {
  const { hasPerm } = useAuth();
  const [pls, setPls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  useEffect(() => {
    api.get("/odoo/pricelists").then((r) => { setPls(r.data.data); setLoading(false); })
      .catch((e) => { toast.error(apiError(e)); setLoading(false); });
  }, []);
  const filtered = pls.filter((p) => !q || (p.name || "").toLowerCase().includes(q.toLowerCase()));
  return (
    <Modal open onClose={onClose} className="pricelist-create-dialog" title="Kelola Pricelist"
      subtitle="Lihat & edit daftar harga, atau buat pricelist baru" testId="pricelist-manager-dialog"
      footer={hasPerm("pricelists:create") && <button className="rj-btn rj-btn-primary" onClick={onCreate} data-testid="pl-create-new-btn"><Plus size={16} /> Buat Baru</button>}>
      <label className="pricelist-search"><Search /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari pricelist…" data-testid="pl-manager-search" /></label>
      {loading ? <div className="rj-loading"><Loader2 className="spin" /> Memuat pricelist…</div> : (
        <div className="pl-manager-list" data-testid="pl-manager-list">
          {filtered.map((p) => (
            <button className="pl-manager-row" key={p.odoo_id} onClick={() => onEdit(p.odoo_id)} data-testid={`pl-row-${p.odoo_id}`}>
              <span className="pl-manager-icon"><Tag size={16} /></span>
              <div className="pl-manager-main"><strong>{p.name}</strong><small>{p.item_count} item · {p.currency || "IDR"}</small></div>
              <ChevronRight size={16} />
            </button>
          ))}
          {filtered.length === 0 && <div className="rec-empty">Tidak ada pricelist.</div>}
        </div>
      )}
    </Modal>
  );
}

function PricelistEditor({ odooId, onClose, onSaved }) {
  const { hasPerm } = useAuth();
  const canEdit = hasPerm("pricelists:create");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [currency, setCurrency] = useState("IDR");
  const [items, setItems] = useState([]);
  const [extras, setExtras] = useState(0);
  const [q, setQ] = useState("");
  const [products, setProducts] = useState([]);
  const [addQ, setAddQ] = useState("");
  const [tab, setTab] = useState("items");
  const [histProduct, setHistProduct] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/odoo/pricelists/${odooId}`);
        const d = r.data.data;
        setName(d.name || ""); setCurrency(d.currency || "IDR");
        setItems((d.items || []).map((it) => ({ product_odoo_id: it.product_odoo_id, product_name: it.product_name, default_code: it.default_code, list_price: it.list_price, price: String(it.fixed_price || "") })));
        setExtras(d.extras_count || 0);
      } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
    })();
  }, [odooId]);

  useEffect(() => { if (canEdit) api.get("/odoo/master").then((r) => setProducts(r.data.data.products || [])).catch(() => {}); }, [canEdit]);

  const setPrice = (pid, val) => setItems((xs) => xs.map((x) => x.product_odoo_id === pid ? { ...x, price: val } : x));
  const removeItem = (pid) => setItems((xs) => xs.filter((x) => x.product_odoo_id !== pid));
  const addProduct = (p) => {
    if (items.some((x) => x.product_odoo_id === p.odoo_id)) { toast.message("Produk sudah ada di pricelist"); return; }
    setItems((xs) => [{ product_odoo_id: p.odoo_id, product_name: p.name, default_code: p.default_code, list_price: p.list_price, price: String(p.list_price || "") }, ...xs]);
    setAddQ("");
  };

  const save = async () => {
    if (!name.trim()) { toast.error("Nama pricelist wajib diisi"); return; }
    const payloadItems = items.filter((x) => Number(x.price) > 0).map((x) => ({ product_odoo_id: x.product_odoo_id, fixed_price: Number(x.price) }));
    setSaving(true);
    try {
      const r = await api.patch(`/odoo/pricelists/${odooId}`, { name, items: payloadItems });
      if (r.data.meta?.odoo_warning) toast.warning(r.data.meta.odoo_warning);
      else toast.success("Pricelist disimpan & disinkronkan ke Odoo");
      onSaved?.();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const filtered = items.filter((x) => !q || (x.product_name || "").toLowerCase().includes(q.toLowerCase()) || (x.default_code || "").toLowerCase().includes(q.toLowerCase()));
  const addMatches = addQ ? products.filter((p) => (p.name.toLowerCase().includes(addQ.toLowerCase()) || (p.default_code || "").toLowerCase().includes(addQ.toLowerCase())) && !items.some((x) => x.product_odoo_id === p.odoo_id)).slice(0, 8) : [];

  return (
    <Modal open onClose={onClose} className="pricelist-create-dialog" title="Edit Pricelist"
      subtitle={canEdit ? "Ubah nama & harga tetap per produk — tersimpan ke Odoo" : "Lihat daftar harga"} testId="pricelist-editor-dialog"
      footer={canEdit && <>
        <button className="rj-btn rj-btn-ghost" onClick={onClose}>Batal</button>
        <button className="rj-btn rj-btn-green" onClick={save} disabled={saving || loading} data-testid="pl-save-btn">{saving ? <Loader2 size={16} className="spin" /> : <><Save size={16} /> Simpan & Sinkronkan</>}</button>
      </>}>
      {loading ? <div className="rj-loading"><Loader2 className="spin" /> Memuat pricelist…</div> : (
        <div className="pricelist-create-form">
          <div className="pricelist-create-top">
            <label>Nama Pricelist<input value={name} onChange={(e) => setName(e.target.value)} disabled={!canEdit} data-testid="pl-edit-name" /></label>
            <label>Mata Uang<input value={currency} disabled /></label>
          </div>
          <div className="pl-tabs" data-testid="pl-tabs">
            <button className={tab === "items" ? "active" : ""} onClick={() => setTab("items")} data-testid="pl-tab-items"><Tag size={13} /> Daftar Harga</button>
            <button className={tab === "history" ? "active" : ""} onClick={() => { setHistProduct(null); setTab("history"); }} data-testid="pl-tab-history"><History size={13} /> Riwayat Harga</button>
          </div>
          {tab === "history" ? (
            <PriceHistory odooId={odooId} product={histProduct} onClearProduct={() => setHistProduct(null)} />
          ) : (<>
          {canEdit && (
            <div className="pl-add-product">
              <label className="pricelist-search"><Search /><input value={addQ} onChange={(e) => setAddQ(e.target.value)} placeholder="Tambah produk ke pricelist…" data-testid="pl-add-search" /></label>
              {addMatches.length > 0 && (
                <div className="pl-add-suggest" data-testid="pl-add-suggest">
                  {addMatches.map((p) => (
                    <button key={p.odoo_id} onClick={() => addProduct(p)} data-testid={`pl-add-${p.odoo_id}`}>
                      <span><strong>{p.name}</strong><small>{p.default_code} · list {rupiah(p.list_price)}</small></span><Plus size={14} />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <label className="pricelist-search"><Search /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari produk di pricelist…" data-testid="pl-filter-search" /></label>
          <div className="pricelist-product-list">
            {filtered.length === 0 ? <div className="rec-empty">Belum ada harga produk di pricelist ini.</div> : (
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <tbody>
                  {filtered.map((x) => (
                    <tr key={x.product_odoo_id} style={{ borderBottom: "1px solid #eef2f0" }} data-testid={`pl-item-${x.product_odoo_id}`}>
                      <td style={{ padding: 10 }}><strong style={{ fontSize: 13 }}>{x.product_name}</strong><small style={{ display: "block", color: "#718078" }}>{x.default_code || "-"}{x.list_price != null ? ` · list ${rupiah(x.list_price)}` : ""}</small></td>
                      <td style={{ padding: 10, width: 170 }}>
                        <div className="price-input"><b>Rp</b><input type="number" value={x.price} onChange={(e) => setPrice(x.product_odoo_id, e.target.value)} disabled={!canEdit} data-testid={`pl-price-${x.product_odoo_id}`} /></div>
                      </td>
                      <td style={{ padding: 10, width: 40 }}><button className="rj-icon-btn" onClick={() => { setHistProduct({ id: x.product_odoo_id, name: x.product_name }); setTab("history"); }} data-testid={`pl-history-${x.product_odoo_id}`} title="Riwayat harga produk ini"><History size={14} /></button></td>
                      {canEdit && <td style={{ padding: 10, width: 40 }}><button className="rj-icon-btn danger" onClick={() => removeItem(x.product_odoo_id)} data-testid={`pl-remove-${x.product_odoo_id}`} title="Hapus dari pricelist"><Trash2 size={14} /></button></td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          {extras > 0 && <p className="pl-extra-note">+{extras} aturan lain (diskon %, kategori, tier kuantitas, dsb.) tidak ditampilkan dan tidak diubah.</p>}
          </>)}
        </div>
      )}
    </Modal>
  );
}

const PH_KIND = { create: "Ditambahkan", delete: "Dihapus", rename: "Ganti nama" };

function phKind(h) {
  if (h.change_type === "update") return (h.new_price ?? 0) > (h.old_price ?? 0) ? { t: "Harga naik", c: "up" } : { t: "Harga turun", c: "down" };
  return { t: PH_KIND[h.change_type] || h.change_type, c: h.change_type };
}

function PriceHistory({ odooId, product, onClearProduct }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    setRows(null);
    api.get(`/odoo/pricelists/${odooId}/history`, { params: { product_odoo_id: product?.id || undefined } })
      .then((r) => setRows(r.data.data)).catch((e) => { toast.error(apiError(e)); setRows([]); });
  }, [odooId, product]);

  return (
    <div className="price-history" data-testid="price-history">
      {product && (
        <div className="ph-filter" data-testid="ph-filter">Produk: <b>{product.name}</b>
          <button onClick={onClearProduct} data-testid="ph-filter-clear"><X size={12} /> Semua produk</button>
        </div>
      )}
      {rows === null ? <div className="rj-loading"><Loader2 className="spin" /> Memuat riwayat…</div> : rows.length === 0 ? (
        <div className="rec-empty" data-testid="ph-empty">Belum ada riwayat perubahan harga. Perubahan dari aplikasi & Odoo akan tercatat di sini.</div>
      ) : (
        <div className="ph-list">
          {rows.map((h) => {
            const k = phKind(h);
            return (
              <div className="ph-row" key={h.id} data-testid={`ph-row-${h.id}`}>
                <div className="ph-main">
                  <strong>{h.change_type === "rename" ? "Nama pricelist" : h.product_name || "—"}</strong>
                  <span className="ph-values">
                    {h.change_type === "rename" ? <>{h.old_name} <ArrowRight size={12} /> {h.new_name}</> : <>
                      {h.old_price != null ? rupiah(h.old_price) : "—"} <ArrowRight size={12} /> {h.new_price != null ? <b>{rupiah(h.new_price)}</b> : "dihapus"}
                    </>}
                  </span>
                </div>
                <div className="ph-meta">
                  <span className={`ph-kind ${k.c}`}>{k.t}</span>
                  <span className={`ph-src ${h.source}`}>{h.source === "odoo" ? "Odoo" : "Aplikasi"}</span>
                  <small>{h.actor_name || h.actor_email || (h.source === "odoo" ? "Pengguna Odoo" : "—")} · {fmtDateTime(h.created_at)}</small>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
